import { Link, useParams, Navigate } from "react-router-dom";
import { getCategory } from "../actions/getCategory";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import ShopsSection from "../components/home/shops-section";
import ProductsSection from "../components/home/products-section";
import LoadingScreen from "../components/loading-screen";
import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";

export default function Category() {
    const { categoryId } = useParams();
    const [subCategoryIndex, setSubCategoryIndex] = useState(0);
    const { t } = useTranslation();

    // hide adahy category
    const { isAndroid, isIOS } = useMemo(() => {
        if (typeof window === "undefined") {
            return {
                isAndroid: false,
                isIOS: false,
            };
        }

        const userAgent = navigator.userAgent;
        return {
            isAndroid: /Android/i.test(userAgent),
            isIOS: /iPhone|iPad|iPod/i.test(userAgent),
        };
    }, []);
    const isBlockedCategory = categoryId === "2";
    useEffect(() => {
        if (!isBlockedCategory) return;

        if (isIOS) {
            window.location.href =
                "https://apps.apple.com/ae/app/zabehaty-%D8%B0%D8%A8%D9%8A%D8%AD%D8%AA%D9%8A/id1227769641";
        }

        if (isAndroid) {
            window.location.href =
                "https://play.google.com/store/apps/details?id=com.atp.zabehati";
        }
    }, [isBlockedCategory, isIOS, isAndroid]);

    const { data, isLoading } = useQuery({
        queryKey: ["global", "category", { categoryId }],
        queryFn: () =>
            getCategory({
                categoryId: categoryId || "",
            }),

        enabled: !isBlockedCategory,
    });

    if (isBlockedCategory) {
        return <Navigate to="/" replace />;
    }

    if (isLoading) return <LoadingScreen />;
    if (!data) return null;

    // Meta tags for SEO
    const categoryName = data.name;
    const subCategoryNames = data.sub_categories?.map((s) => s.name).join(", ");
    const productNames = data.products?.map((p) => p.name).join(", ");
    const metaTitle = `Buy ${categoryName} | ${subCategoryNames}`;
    const metaDescription = `Discover ${productNames} in ${categoryName}. Explore top-quality options like ${subCategoryNames}. Shop online in the UAE.`;
    const metaKeywords = `${categoryName}, ${subCategoryNames}, ${productNames}, Zabehaty, meat delivery, UAE`;
    const ogImage =
        data.slider?.[0]?.image ||
        data.sub_categories?.[0]?.image ||
        "https://www.zabehaty.com/default-og-image.jpg";
    const ogUrl = window.location.href;

    return (
        <>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta name="keywords" content={metaKeywords} />

                {/* Open Graph / Facebook */}
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:site_name" content="Zabehaty" />

                {/* Snapchat meta (if required) */}
                {/* <meta property="snapchat:sticker" content="https://kit.snapchat.com/ckweb/test/image.png" /> */}
            </Helmet>

            <div className="container flex flex-col gap-y-10 pb-28 pt-14 xl:gap-y-20">
                <h2 className="text-center text-[40px] font-medium text-[rgba(23,23,23,1)]">
                    {data.name}
                </h2>
                {data.sub_categories.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-6">
                        {data.sub_categories?.map((subCategory, idx) => (
                            <button
                                onClick={() => setSubCategoryIndex(idx)}
                                key={subCategory.id}
                                className="flex flex-col items-center"
                            >
                                <img
                                    src={subCategory.image}
                                    alt={subCategory.name}
                                    className="mb-3 size-[80px] rounded-full object-cover xl:mb-6 xl:size-[146px]"
                                />
                                <span className="text-[26px] text-[rgba(64,64,64,1)]">
                                    {subCategory.name}
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {data.sub_categories.length > 0 &&
                    (data.sub_categories[subCategoryIndex]?.sub_categories
                        ?.length ?? 0) > 0 && (
                        <div>
                            <h3 className="mb-3 text-2xl font-medium text-[rgba(23,23,23,1)] xl:mb-6 xl:text-[28px]">
                                <span className="ltr:hidden">
                                    {t("species")}
                                </span>{" "}
                                {data.sub_categories[subCategoryIndex].name}{" "}
                                <span className="rtl:hidden">
                                    {t("species")}
                                </span>
                            </h3>
                            <div className="flex flex-wrap gap-6">
                                {data.sub_categories[
                                    subCategoryIndex
                                ]?.sub_categories?.map((subCategory) => (
                                    <Link
                                        to={`/product/${subCategory.product_id}`}
                                        key={subCategory.id}
                                        className="flex flex-col items-center rounded-[11px] bg-[rgba(207,219,211,1)] p-3 shadow-[0px_5.5px_5.5px_0px_rgba(182,182,182,0.15)] xl:px-4 xl:py-4"
                                    >
                                        <img
                                            src={subCategory.image}
                                            alt={subCategory.name}
                                            className="mb-0.5 size-[90px] object-cover xl:size-[146px]"
                                        />
                                        <span className="text-xl text-[rgba(23,23,23,1)]">
                                            {subCategory.name}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                {data.products.length > 0 && (
                    <ProductsSection
                        products={data.products}
                        title={t("products")}
                    />
                )}
                {data.shops.length > 0 && (
                    <ShopsSection
                        shops={data.shops}
                        title={data.shop_section_name}
                    />
                )}
            </div>
        </>
    );
}
